import * as React from "react";

import { cn } from "../lib/utils";
import { Label } from "./label";

/**
 * Field — a pure composition (no Radix primitive): a `Label` wired to a single
 * control child, plus optional hint and error slots. All text arrives via props
 * so the `@webtemplate/ui` package stays i18n-free (D-20); the consuming app
 * supplies react-i18next-resolved strings.
 *
 * The control child is cloned to inject the a11y wiring the consumer would
 * otherwise repeat by hand: `id` (label association), `aria-describedby` (hint +
 * error ids, merged with any the consumer already set), and `aria-invalid` when
 * an error is present. label/hint/error render as React text children only —
 * never dangerouslySetInnerHTML — so React's escaping is the XSS control
 * (threat T-09.3-08).
 */
export interface FieldProps {
  /** Visible, i18n-resolved label text. */
  label: string;
  /** Id applied to the control and referenced by the label's htmlFor. */
  htmlFor: string;
  /** Optional error message. When set, the control is marked aria-invalid. */
  error?: string;
  /** Optional helper text rendered under the control. */
  hint?: string;
  /** The single form control (Input, Select trigger, etc.). */
  children: React.ReactElement<{
    id?: string;
    "aria-invalid"?: boolean | "true" | "false";
    "aria-describedby"?: string;
  }>;
  className?: string;
}

function Field({ label, htmlFor, error, hint, children, className }: FieldProps) {
  const hintId = hint ? `${htmlFor}-hint` : undefined;
  const errorId = error ? `${htmlFor}-error` : undefined;
  const describedBy =
    [children.props["aria-describedby"], hintId, errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  const control = React.cloneElement(children, {
    id: htmlFor,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : children.props["aria-invalid"],
  });

  return (
    <div data-slot="field" className={cn("grid gap-2", className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {control}
      {hint ? (
        <p
          id={hintId}
          data-slot="field-hint"
          className="text-muted-foreground text-sm"
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          role="alert"
          data-slot="field-error"
          className="text-destructive text-sm"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export { Field };
